import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query';

import {
  CaseHistoryResponseDto,
  CaseMessageDispatchResponseDto,
  CreateCaseMessageDispatchDto,
  FieldBlockSchemaDto,
  InboxCaseContextDto,
  InboxCaseManagementOptionsDto,
  InboxCaseManagementRequestDto,
  InboxPreferencesDto,
  PagedResponse,
  UpdateInboxPreferencesDto,
  UpdateCustomFieldValue,
} from '@bandido/shared/dtos';

import { Channels } from '@bandido/shared/constants';

import { client } from '@frontend/features/common/services';
import { QueryKeys } from '@frontend/features/common/types';

import { InboxFilterOptions, InboxFilters, InboxPage, InboxUnreadCount } from './types';

export const inboxQueryKeys = {
  all: ['inbox'] as const,
  user: (userId: string) => [...inboxQueryKeys.all, userId] as const,
  lists: (userId: string) => [...inboxQueryKeys.user(userId), 'list'] as const,
  list: (userId: string, filters: InboxFilters) => [...inboxQueryKeys.lists(userId), filters] as const,
  unread: (userId: string) => [...inboxQueryKeys.user(userId), 'unread-count'] as const,
  options: (userId: string, agencies: number[]) =>
    [...inboxQueryKeys.user(userId), 'filter-options', agencies] as const,
  histories: (userId: string) => [...inboxQueryKeys.user(userId), 'history'] as const,
  caseHistory: (userId: string, caseId: number) => [...inboxQueryKeys.histories(userId), caseId] as const,
  history: (userId: string, caseId: number, includeAutomaticStatusChanges: boolean) =>
    [...inboxQueryKeys.caseHistory(userId, caseId), { includeAutomaticStatusChanges }] as const,
  channelHistory: (userId: string, caseId: number, channel: Channels) =>
    [...inboxQueryKeys.caseHistory(userId, caseId), 'channel', channel] as const,
  preferences: (userId: string) => [...inboxQueryKeys.user(userId), 'preferences'] as const,
  contexts: (userId: string) => [...inboxQueryKeys.user(userId), 'context'] as const,
  context: (userId: string, caseId: number) => [...inboxQueryKeys.contexts(userId), caseId] as const,
  managementOptions: (userId: string, caseId: number) =>
    [...inboxQueryKeys.user(userId), 'management-options', caseId] as const,
  customFields: (userId: string, caseId: number, workflowId?: string) =>
    [...inboxQueryKeys.user(userId), 'custom-fields', caseId, ...(workflowId ? [workflowId] : [])] as const,
  dispatch: (userId: string, caseId: number, idempotencyKey: string) =>
    [...inboxQueryKeys.user(userId), 'message-dispatch', caseId, idempotencyKey] as const,
};

const listParams = (filters: InboxFilters, cursor?: string) => ({
  limit: 50,
  cursor,
  search: filters.search || undefined,
  agencies: filters.agencies.length ? filters.agencies.join('|') : undefined,
  assignment: filters.assignment,
  workflowIds: filters.workflowIds.length ? filters.workflowIds.join('|') : undefined,
  stepIds: filters.stepIds.length ? filters.stepIds.join('|') : undefined,
  channel: filters.channel,
  unreadOnly: filters.unreadOnly || undefined,
});

export const useInboxItems = (userId: string | undefined, filters: InboxFilters, enabled = true) =>
  useInfiniteQuery(
    inboxQueryKeys.list(userId || 'anonymous', filters),
    async ({ pageParam }) => (await client.get<InboxPage>('inbox', { params: listParams(filters, pageParam) })).data,
    {
      getNextPageParam: (page) => page.nextCursor || undefined,
      enabled: enabled && Boolean(userId),
      staleTime: 15_000,
      refetchOnWindowFocus: true,
    },
  );

export const useInboxUnreadCount = (userId: string | undefined, enabled = true) =>
  useQuery(
    inboxQueryKeys.unread(userId || 'anonymous'),
    async () => (await client.get<InboxUnreadCount>('inbox/unread-count')).data,
    {
      enabled: enabled && Boolean(userId),
      staleTime: 15_000,
      refetchOnWindowFocus: true,
    },
  );

export const useInboxFilterOptions = (userId: string | undefined, agencies: number[], enabled = true) =>
  useQuery(
    inboxQueryKeys.options(userId || 'anonymous', agencies),
    async () =>
      (
        await client.get<InboxFilterOptions>('inbox/filter-options', {
          params: { agencies: agencies.length ? agencies.join('|') : undefined },
        })
      ).data,
    { enabled: enabled && Boolean(userId), staleTime: 60_000, refetchOnWindowFocus: true },
  );

export const useInboxCaseHistory = (
  userId: string | undefined,
  caseId: number | null,
  includeAutomaticStatusChanges: boolean,
) =>
  useInfiniteQuery(
    inboxQueryKeys.history(userId || 'anonymous', caseId || 0, includeAutomaticStatusChanges),
    async ({ pageParam = 1 }) =>
      (
        await client.get<PagedResponse<CaseHistoryResponseDto>>(`case/${caseId}/history`, {
          params: { currentPage: pageParam, pageSize: 30, includeAutomaticStatusChanges },
        })
      ).data,
    {
      enabled: Boolean(userId && caseId),
      getNextPageParam: (page) =>
        Number(page.currentPage) < Number(page.pages) ? Number(page.currentPage) + 1 : undefined,
      select: (data) => ({
        pages: [...data.pages].reverse(),
        pageParams: [...data.pageParams].reverse(),
      }),
      refetchOnWindowFocus: true,
    },
  );

export const useInboxPreferences = (userId: string | undefined, enabled = true) =>
  useQuery(
    inboxQueryKeys.preferences(userId || 'anonymous'),
    async () => (await client.get<InboxPreferencesDto>('notifications/inbox-preferences')).data,
    { enabled: enabled && Boolean(userId), staleTime: 60_000, refetchOnWindowFocus: true },
  );

export const useUpdateInboxPreferences = (userId: string | undefined) => {
  const queryClient = useQueryClient();
  const key = inboxQueryKeys.preferences(userId || 'anonymous');
  return useMutation(
    (update: UpdateInboxPreferencesDto) => client.patch<InboxPreferencesDto>('notifications/inbox-preferences', update),
    {
      onMutate: async (update) => {
        await queryClient.cancelQueries(key);
        const previous = queryClient.getQueryData<InboxPreferencesDto>(key);
        queryClient.setQueryData<InboxPreferencesDto>(key, {
          showAutomaticStatusChanges: false,
          notifyAutomaticStatusChanges: false,
          savedViews: [],
          savedViewsVersion: 0,
          ...previous,
          ...update,
          ...(update.savedViews === undefined ? {} : { savedViewsVersion: (previous?.savedViewsVersion ?? 0) + 1 }),
        });
        return { previous };
      },
      onError: (_error, _update, context) => {
        queryClient.setQueryData<InboxPreferencesDto>(
          key,
          context?.previous ?? {
            showAutomaticStatusChanges: false,
            notifyAutomaticStatusChanges: false,
            savedViews: [],
            savedViewsVersion: 0,
          },
        );
      },
      onSuccess: ({ data }) => {
        queryClient.setQueryData(key, data);
      },
      onSettled: (_data, _error, update) => {
        queryClient.invalidateQueries(key);
        if (userId && update.showAutomaticStatusChanges !== undefined) {
          queryClient.invalidateQueries(inboxQueryKeys.histories(userId));
        }
      },
    },
  );
};

export const useInboxCaseChannelHistory = (userId: string | undefined, caseId: number | null, channel: Channels) =>
  useInfiniteQuery(
    inboxQueryKeys.channelHistory(userId || 'anonymous', caseId || 0, channel),
    async ({ pageParam = 1 }) =>
      (
        await client.get<PagedResponse<CaseHistoryResponseDto>>(`case/${caseId}/history`, {
          params: { currentPage: pageParam, pageSize: 30, channel },
        })
      ).data,
    {
      enabled: Boolean(userId && caseId),
      getNextPageParam: (page) =>
        Number(page.currentPage) < Number(page.pages) ? Number(page.currentPage) + 1 : undefined,
      select: (data) => ({
        pages: [...data.pages].reverse(),
        pageParams: [...data.pageParams].reverse(),
      }),
      refetchOnWindowFocus: true,
    },
  );

export const useInboxCaseContext = (userId: string | undefined, caseId: number | null) =>
  useQuery(
    inboxQueryKeys.context(userId || 'anonymous', caseId || 0),
    async () => (await client.get<InboxCaseContextDto>(`inbox/${caseId}/context`)).data,
    { enabled: Boolean(userId && caseId), refetchOnWindowFocus: true },
  );

export const useInboxCaseManagementOptions = (userId: string | undefined, caseId: number | null) =>
  useQuery(
    inboxQueryKeys.managementOptions(userId || 'anonymous', caseId || 0),
    async () => (await client.get<InboxCaseManagementOptionsDto>(`inbox/${caseId}/management-options`)).data,
    { enabled: Boolean(userId && caseId), refetchOnWindowFocus: true },
  );

export const useInboxCaseCustomFields = (
  userId: string | undefined,
  caseId: number | null,
  workflowId: string | undefined,
) =>
  useQuery(
    inboxQueryKeys.customFields(userId || 'anonymous', caseId || 0, workflowId),
    async () => (await client.get<FieldBlockSchemaDto[]>(`case/${caseId}/custom-fields`)).data,
    { enabled: Boolean(userId && caseId), refetchOnWindowFocus: true },
  );

export const useUpdateInboxCaseCustomField = (userId: string | undefined, caseId: number) => {
  const queryClient = useQueryClient();
  const key = inboxQueryKeys.customFields(userId || 'anonymous', caseId);
  return useMutation((field: UpdateCustomFieldValue) => client.put(`case/${caseId}/custom-fields`, [field]), {
    onSuccess: () => queryClient.invalidateQueries(key),
    onError: () => queryClient.invalidateQueries(key),
    onSettled: () => queryClient.invalidateQueries([QueryKeys.getCaseById, caseId]),
  });
};

export const useManageInboxCase = (userId: string | undefined, caseId: number, workflowId?: string) => {
  const queryClient = useQueryClient();
  const contextKey = inboxQueryKeys.context(userId || 'anonymous', caseId);
  const customFieldsKey = inboxQueryKeys.customFields(userId || 'anonymous', caseId, workflowId);
  const customFieldsPrefix = inboxQueryKeys.customFields(userId || 'anonymous', caseId);
  return useMutation(
    (request: InboxCaseManagementRequestDto) =>
      client.patch<InboxCaseContextDto>(`inbox/${caseId}/management`, request),
    {
      onMutate: async (request) => {
        await queryClient.cancelQueries(contextKey);
        if (request.workflowId !== undefined) await queryClient.cancelQueries(customFieldsKey);
        const previous = queryClient.getQueryData<InboxCaseContextDto>(contextKey);
        const options = queryClient.getQueryData<InboxCaseManagementOptionsDto>(
          inboxQueryKeys.managementOptions(userId || 'anonymous', caseId),
        );
        if (previous) {
          const optimistic = { ...previous };
          if (request.status !== undefined) optimistic.status = request.status;
          if (request.stepId !== undefined) {
            const step = options?.steps.find(({ id }) => id === request.stepId);
            optimistic.step = step ? { id: step.id, name: step.name } : previous.step;
          }
          if (request.agentId !== undefined) {
            const advisor = options?.agents.find(({ id }) => id === request.agentId);
            optimistic.advisor =
              request.agentId === null ? null : advisor ? { id: advisor.id, name: advisor.name } : previous.advisor;
          }
          queryClient.setQueryData(contextKey, optimistic);
        }
        return { previous };
      },
      onError: (_error, _request, rollback) => {
        if (rollback?.previous) queryClient.setQueryData(contextKey, rollback.previous);
      },
      onSuccess: ({ data }, request) => {
        if (request.workflowId !== undefined) queryClient.setQueryData(customFieldsKey, []);
        queryClient.setQueryData(contextKey, data);
      },
      onSettled: () => {
        if (userId) {
          queryClient.invalidateQueries(inboxQueryKeys.contexts(userId));
          queryClient.invalidateQueries(inboxQueryKeys.lists(userId));
          queryClient.invalidateQueries(inboxQueryKeys.managementOptions(userId, caseId));
          queryClient.invalidateQueries(customFieldsPrefix);
        }
        queryClient.invalidateQueries([QueryKeys.getCaseById, caseId]);
        queryClient.invalidateQueries(QueryKeys.getAllCases);
      },
    },
  );
};

export const useMarkInboxCaseRead = (userId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ caseId, throughHistoryId }: { caseId: number; throughHistoryId: number }) =>
      client.put(`case-history/${caseId}/read`, { throughHistoryId }),
    {
      onSuccess: () => {
        if (!userId) return;
        queryClient.invalidateQueries(inboxQueryKeys.lists(userId));
        queryClient.invalidateQueries(inboxQueryKeys.unread(userId));
      },
    },
  );
};

export const useCreateInboxMessageDispatch = (userId: string | undefined, caseId: number) => {
  const queryClient = useQueryClient();
  return useMutation(
    (request: CreateCaseMessageDispatchDto) =>
      client.post<CaseMessageDispatchResponseDto>(`inbox/${caseId}/messages`, request),
    {
      onSuccess: ({ data }) => {
        if (!userId) return;
        queryClient.setQueryData(inboxQueryKeys.dispatch(userId, caseId, data.idempotencyKey), data);
        if (data.status === 'sent') queryClient.invalidateQueries(inboxQueryKeys.caseHistory(userId, caseId));
      },
    },
  );
};

export const useInboxMessageDispatch = (
  userId: string | undefined,
  caseId: number,
  idempotencyKey: string | undefined,
) =>
  useQuery(
    inboxQueryKeys.dispatch(userId || 'anonymous', caseId, idempotencyKey || 'pending'),
    async () =>
      (await client.get<CaseMessageDispatchResponseDto>(`inbox/${caseId}/message-dispatches/${idempotencyKey}`)).data,
    {
      enabled: Boolean(userId && idempotencyKey),
      refetchInterval: (data) => (data?.status === 'sending' ? 2_000 : false),
      refetchOnWindowFocus: true,
    },
  );
